import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, RotateCw, Send } from "lucide-react";
import { AdminRole, WebhookDeliveryStatus, WebhookEvent } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { columnFilter, DateRangeFilterFields, enumOptions, SelectFilterField } from "@/components/column-filter";
import { StatCard } from "@/components/stat-card";
import { StatusBadge } from "@/components/status-badge";
import { CopyButton } from "@/components/copy-button";
import { MerchantCell } from "@/components/merchant-cell";
import { JsonViewer } from "@/components/json-viewer";
import { STATUS_DOT_COLOR, WEBHOOK_DELIVERY_STATUS_COLOR, WEBHOOK_EVENT_COLOR, type StatusColor } from "@/lib/status-colors";
import { useTableState, type TableSearch } from "@/lib/table-state";
import { formatStatusLabel } from "@/lib/utils";
import { hasRole } from "@/lib/roles";
import { ApiError } from "@/api/client";
import { getWebhookDelivery, listWebhookDeliveries, retryWebhookDelivery } from "@/api/webhook-deliveries";
import { listMerchants } from "@/api/merchants";
import { type ListWebhookDeliveriesParams, type WebhookDeliveryListItem } from "@/api/types";

// Real implementation of Task 30 Part F, replacing the Task-22 placeholder:
// the GLOBAL, cross-merchant webhook delivery listing (TECH_VIEWER-gated) —
// distinct from the merchant-scoped listing on the merchant detail page
// (Task 25). Same real `useTableState` + `DataTable` shape as
// _app.invoices.tsx/_app.refunds.tsx: server-side pagination + filters, no
// configurable sort (the DTO has none).
type WebhookDeliveryFilters = {
  status?: WebhookDeliveryStatus;
  eventType?: WebhookEvent;
  merchantId?: string;
  from?: string;
  to?: string;
};

const DEFAULT_FILTERS: WebhookDeliveryFilters = {};

/** Plain type-guarded passthrough — mirrors _app.invoices.tsx's `validateSearch` house style. */
function validateWebhookDeliveriesSearch(
  search: Record<string, unknown>,
): TableSearch<WebhookDeliveryFilters> {
  return {
    page: typeof search.page === "number" ? search.page : undefined,
    pageSize: typeof search.pageSize === "number" ? search.pageSize : undefined,
    status: typeof search.status === "string" ? (search.status as WebhookDeliveryStatus) : undefined,
    eventType: typeof search.eventType === "string" ? (search.eventType as WebhookEvent) : undefined,
    merchantId: typeof search.merchantId === "string" ? search.merchantId : undefined,
    from: typeof search.from === "string" ? search.from : undefined,
    to: typeof search.to === "string" ? search.to : undefined,
  };
}

export const Route = createFileRoute("/_app/webhook-deliveries")({
  validateSearch: validateWebhookDeliveriesSearch,
  component: WebhookDeliveriesPage,
});

// ─── formatting helpers ─────────────────────────────────────────────────────

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

/** 2xx = success, 3xx = info, 4xx/5xx = danger, no response yet = neutral. */
function responseCodeColor(code: number): StatusColor {
  if (code >= 200 && code < 300) return "success";
  if (code >= 300 && code < 400) return "info";
  return "danger";
}

/** The plain-language outcome behind a response code — same bucketing as `responseCodeColor`,
 * so the detail dialog's hero stat reads as "404 · Client error" instead of a bare number an
 * operator has to mentally classify themselves. */
function responseCodeOutcome(code: number): string {
  if (code >= 200 && code < 300) return "Success";
  if (code >= 300 && code < 400) return "Redirect";
  if (code >= 400 && code < 500) return "Client error";
  return "Server error";
}

function ResponseCode({ code }: { code: number | null }) {
  if (code === null) return <span className="text-muted-foreground">—</span>;
  return (
    <span
      className="font-semibold"
      style={{ color: STATUS_DOT_COLOR[responseCodeColor(code)], fontVariantNumeric: "tabular-nums" }}
    >
      {code}
    </span>
  );
}

// ─── row-detail dialog ──────────────────────────────────────────────────────
//
// Fetch-on-open, same convention as every other detail dialog in this app.

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium" style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </dd>
    </div>
  );
}

/** Uppercase eyebrow label above a group of `DetailRow`s — same treatment used across the app's other detail dialogs. */
function SectionLabel({ children }: { children: ReactNode }) {
  return <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{children}</h3>;
}

/** A tinted callout for the one thing on this dialog that most needs an operator's eye —
 * the last error string a failed/retrying delivery recorded. Free text (whatever the callback
 * endpoint or the fetch itself reported), so it gets its own full-width block rather than being
 * squeezed into a DetailRow's right-aligned value column, which wraps badly for anything longer
 * than a couple of words. */
function ErrorNotice({ message }: { message: string }) {
  const hex = STATUS_DOT_COLOR.danger;
  return (
    <div
      className="flex items-start gap-2 rounded-md p-3 text-sm"
      style={{ backgroundColor: `${hex}0D`, color: hex }}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <p className="break-words">{message}</p>
    </div>
  );
}

function retryErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) return "This webhook has already been retried.";
  if (err instanceof ApiError) return err.message;
  return "Retry failed.";
}

function WebhookDeliveryDetailDialog({
  deliveryId,
  canRetry,
  onOpenChange,
}: {
  deliveryId: string | null;
  canRetry: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { data, isPending, isError } = useQuery({
    queryKey: ["webhook-delivery", deliveryId],
    queryFn: () => getWebhookDelivery(deliveryId!),
    enabled: deliveryId !== null,
    // feature-041: after a manual retry, poll until the single attempt settles,
    // then refresh the list and the sidebar badge so they show the outcome too.
    refetchInterval: (query) => {
      const d = query.state.data;
      return d?.manualRetryAt && d.status === WebhookDeliveryStatus.PENDING ? 3_000 : false;
    },
  });
  const seen = useRef<{ id: string; status: WebhookDeliveryStatus } | null>(null);
  useEffect(() => {
    if (!data) return;
    const prev = seen.current;
    if (prev && prev.id === data.id && prev.status !== data.status) {
      void queryClient.invalidateQueries({ queryKey: ["webhook-deliveries"] });
      void queryClient.invalidateQueries({ queryKey: ["webhook-deliveries-exhausted"] });
    }
    seen.current = { id: data.id, status: data.status };
  }, [data, queryClient]);

  // feature-041: one more attempt for an EXHAUSTED delivery. The detail, the
  // list and the sidebar badge all change, so all three are refreshed.
  const retry = useMutation({
    mutationFn: () => retryWebhookDelivery(deliveryId!),
    onSuccess: (detail) => {
      queryClient.setQueryData(["webhook-delivery", deliveryId], detail);
      toast.success("Retry queued — the webhook will be sent again within a few seconds.");
    },
    onError: (err) => toast.error(retryErrorMessage(err)),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["webhook-delivery", deliveryId] });
      void queryClient.invalidateQueries({ queryKey: ["webhook-deliveries"] });
      void queryClient.invalidateQueries({ queryKey: ["webhook-deliveries-exhausted"] });
    },
  });

  // One relevant "what happens next / what happened" fact, not two rows that are each null half
  // the time: PENDING has a next-attempt ETA, SUCCEEDED has a delivered time, EXHAUSTED has
  // neither — no more attempts are coming, which is itself the fact worth stating.
  const timelineRow =
    data &&
    (data.status === WebhookDeliveryStatus.SUCCEEDED
      ? { label: "Delivered", value: formatTimestamp(data.deliveredAt) }
      : data.status === WebhookDeliveryStatus.PENDING
        ? { label: "Next attempt", value: formatTimestamp(data.nextAttemptAt) }
        : { label: "Next attempt", value: "None — retries exhausted" });

  return (
    <Dialog open={deliveryId !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        {isPending && <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>}
        {isError && !isPending && (
          <div className="py-10 text-center text-sm text-muted-foreground">Failed to load delivery.</div>
        )}

        {data && !isPending && !isError && (
          <>
            <DialogHeader>
              <div className="flex flex-wrap items-center gap-2 pr-6">
                <DialogTitle>Webhook delivery</DialogTitle>
                <StatusBadge label={formatStatusLabel(data.status)} color={WEBHOOK_DELIVERY_STATUS_COLOR[data.status]} />
                <StatusBadge label={data.eventType} color={WEBHOOK_EVENT_COLOR[data.eventType]} />
              </div>
              <DialogDescription asChild>
                <div className="flex flex-col gap-0.5">
                  <span>{data.merchantBusinessName}</span>
                  {data.invoicePublicId && (
                    <span className="flex items-center gap-1 font-mono text-xs">
                      {data.invoicePublicId}
                      <CopyButton value={data.invoicePublicId} label="Copy invoice ID" className="h-4 w-4" />
                    </span>
                  )}
                </div>
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-3">
              <StatCard
                label="Response"
                value={<ResponseCode code={data.lastResponseCode} />}
                subLine={data.lastResponseCode === null ? "No response yet" : responseCodeOutcome(data.lastResponseCode)}
                icon={Send}
                tone={data.lastResponseCode === null ? "neutral" : responseCodeColor(data.lastResponseCode)}
              />
              <StatCard label="Attempts" value={data.attemptCount} />
            </div>

            {data.lastError && <ErrorNotice message={data.lastError} />}

            {canRetry && data.status === WebhookDeliveryStatus.EXHAUSTED && (
              <div className="flex items-center justify-between gap-3 rounded-md border border-border p-3">
                <p className="text-sm text-muted-foreground">Cause fixed? Send this webhook once more.</p>
                <Button size="sm" className="shrink-0" disabled={retry.isPending} onClick={() => retry.mutate()}>
                  <RotateCw className="h-4 w-4" />
                  {retry.isPending ? "Retrying…" : "Retry"}
                </Button>
              </div>
            )}

            <SectionLabel>Timeline</SectionLabel>
            <dl className="space-y-2">
              <DetailRow label={timelineRow!.label} value={timelineRow!.value} />
              {data.manualRetryAt && (
                <DetailRow label="Manually retried" value={formatTimestamp(data.manualRetryAt)} />
              )}
            </dl>

            <div className="space-y-2">
              <SectionLabel>Payload</SectionLabel>
              <JsonViewer data={data.payload} defaultCollapsed={false} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── row → dialog-opening click ─────────────────────────────────────────────
//
// DataTable has no onRowClick concept — same cell-filling <button> pattern as
// _app.high-risk.tsx's RowButton / -transactions-table.tsx's RowButton.

function RowButton({ onSelect, children }: { onSelect: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onSelect} className="-m-2 block w-full p-2 text-left">
      {children}
    </button>
  );
}

function WebhookDeliveriesPage() {
  const { admin } = Route.useRouteContext();
  const tableState = useTableState<WebhookDeliveryFilters>({ defaultFilters: DEFAULT_FILTERS });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const params: ListWebhookDeliveriesParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    ...tableState.filters,
  };

  const { data, isPending, isError } = useQuery({
    queryKey: ["webhook-deliveries", params],
    queryFn: () => listWebhookDeliveries(params),
  });

  const { data: merchants } = useQuery({
    queryKey: ["merchants"],
    queryFn: listMerchants,
  });
  const sortedMerchants = [...(merchants ?? [])].sort((a, b) => a.businessName.localeCompare(b.businessName));

  const columns: DataTableColumn<WebhookDeliveryListItem>[] = [
    {
      key: "merchant",
      header: "Merchant",
      filter: columnFilter(tableState, ["merchantId"], (draft, set) => (
        <SelectFilterField
          id="filter-merchant"
          label="Merchant"
          allLabel="All merchants"
          options={sortedMerchants.map((m) => ({ value: m.id, label: m.businessName }))}
          value={draft.merchantId}
          onChange={(v) => set("merchantId", v)}
        />
      )),
      // Not a RowButton like the rest of the row (which opens the delivery
      // detail dialog) — this cell navigates to the merchant page instead.
      cell: (d) => (
        <MerchantCell merchantId={d.merchantId} businessName={d.merchantBusinessName} publicId={d.merchantPublicId} />
      ),
    },
    {
      key: "invoicePublicId",
      header: "Invoice",
      // Null for channel-payment and unattached-deposit deliveries.
      cell: (d) =>
        d.invoiceId && d.invoicePublicId ? (
          <span className="flex items-center gap-1">
            <Link
              to="/invoices/$invoiceId"
              params={{ invoiceId: d.invoiceId }}
              className="group -m-2 block p-2"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="font-mono text-xs group-hover:underline">{d.invoicePublicId}</span>
            </Link>
            <CopyButton value={d.invoicePublicId} label="Copy invoice ID" className="h-5 w-5 text-muted-foreground hover:text-foreground" />
          </span>
        ) : (
          <RowButton onSelect={() => setSelectedId(d.id)}>
            <span className="text-muted-foreground">—</span>
          </RowButton>
        ),
    },
    {
      key: "eventType",
      header: "Event type",
      filter: columnFilter(tableState, ["eventType"], (draft, set) => (
        <SelectFilterField
          id="filter-event-type"
          label="Event type"
          allLabel="All event types"
          options={Object.values(WebhookEvent).map((ev) => ({ value: ev, label: ev }))}
          value={draft.eventType}
          onChange={(v) => set("eventType", v as WebhookEvent | undefined)}
        />
      )),
      cell: (d) => (
        <RowButton onSelect={() => setSelectedId(d.id)}>
          <StatusBadge label={d.eventType} color={WEBHOOK_EVENT_COLOR[d.eventType]} />
        </RowButton>
      ),
    },
    {
      key: "status",
      header: "Status",
      filter: columnFilter(tableState, ["status"], (draft, set) => (
        <SelectFilterField
          id="filter-status"
          label="Status"
          allLabel="All statuses"
          options={enumOptions(Object.values(WebhookDeliveryStatus))}
          value={draft.status}
          onChange={(v) => set("status", v as WebhookDeliveryStatus | undefined)}
        />
      )),
      cell: (d) => (
        <RowButton onSelect={() => setSelectedId(d.id)}>
          <StatusBadge label={formatStatusLabel(d.status)} color={WEBHOOK_DELIVERY_STATUS_COLOR[d.status]} />
        </RowButton>
      ),
    },
    {
      key: "attemptCount",
      header: "Attempts",
      className: "text-right",
      cell: (d) => (
        <RowButton onSelect={() => setSelectedId(d.id)}>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{d.attemptCount}</span>
        </RowButton>
      ),
    },
    {
      key: "lastResponseCode",
      header: "Last response",
      className: "text-right",
      cell: (d) => (
        <RowButton onSelect={() => setSelectedId(d.id)}>
          <ResponseCode code={d.lastResponseCode} />
        </RowButton>
      ),
    },
    {
      key: "nextAttemptAt",
      header: "Next attempt",
      cell: (d) => <RowButton onSelect={() => setSelectedId(d.id)}>{formatTimestamp(d.nextAttemptAt)}</RowButton>,
    },
    {
      key: "deliveredAt",
      header: "Delivered",
      filter: columnFilter(tableState, ["from", "to"], (draft, set) => (
        <DateRangeFilterFields
          idPrefix="filter-date"
          fromLabel="Created from"
          toLabel="Created to"
          from={draft.from}
          to={draft.to}
          onFromChange={(v) => set("from", v)}
          onToChange={(v) => set("to", v)}
        />
      )),
      cell: (d) => <RowButton onSelect={() => setSelectedId(d.id)}>{formatTimestamp(d.deliveredAt)}</RowButton>,
    },
  ];

  const anyFilterActive = Object.values(tableState.filters).some((v) => v !== undefined);

  return (
    <div className="space-y-6">
      <DataTable
        columns={columns}
        items={data?.data ?? []}
        total={data?.total ?? 0}
        page={tableState.page}
        pageSize={tableState.pageSize}
        loading={isPending}
        error={isError ? "Failed to load webhook deliveries." : null}
        onPageChange={(page) => tableState.setPage(page)}
        onPageSizeChange={(pageSize) => tableState.setPageSize(pageSize)}
        filterBar={
          anyFilterActive && (
            <Button type="button" variant="ghost" size="sm" onClick={() => tableState.reset()}>
              Clear filters
            </Button>
          )
        }
        getRowKey={(d) => d.id}
        emptyMessage="No webhook deliveries match your filters."
      />

      <WebhookDeliveryDetailDialog
        deliveryId={selectedId}
        canRetry={hasRole(admin.roles, AdminRole.TECH_MANAGER)}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
      />
    </div>
  );
}
